/* eslint-disable camelcase */
import '../../vendors/bootstrap/dist/css/bootstrap-scoped.css';

import {withRedirects} from '@/lib/withRedirects';
import Bugsnag from '@bugsnag/js';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import React, {useEffect, useRef, useState} from 'react';
import {InView, useInView} from 'react-intersection-observer';
import {useDispatch, useSelector} from 'react-redux';

import ArtistCommission from '../../src/components/artist-commission/artist-commission';
import ArtistContactMe from '../../src/components/artist-contact-me/artist-contact-me';
import Breadcrumb from '../../src/components/breadcrumb/breadcrumb';
import ModalAddToCollection from '../../src/components/modal-add-to-collection/modal-add-to-collection';
import ModalCreateCollection from '../../src/components/modal-create-collection/modal-create-collection';
import ModalMakeOffer from '../../src/components/modal-make-offer/modal-make-offer';
import Print from '../../src/components/print/print';
import ProductAdditionalInfo from '../../src/components/product-additional-info/product-additional-info';
import ProductArtistReviews from '../../src/components/product-artist-reviews/product-artist-reviews';
import ProductArtist from '../../src/components/product-artist/product-artist';
import ProductArtworkDescription from '../../src/components/product-artwork-description/product-artwork-description';
import ProductAttributes from '../../src/components/product-attributes/product-attributes';
import ProductBtnSet from '../../src/components/product-btn-set/product-btn-set';
import ProductCarouselFullScreen from '../../src/components/product-carousel-full-screen/product-carousel-full-screen';
import ProductSummary from '../../src/components/product-summary/product-summary';
import Tab from '../../src/components/tab/tab';
import AddToBasketCue from '../../src/components/tactical/customerCues/addToBasket/addToBasket';
import FreeShipingCue from '../../src/components/tactical/customerCues/freeShipping/freeShipping';
import SaleCountdown from '../../src/components/tactical/saleCountdown/saleCountdown';
import Scarcity from '../../src/components/tactical/scarcity/scarcity';

import type {ManagedShippingProductPageProps} from '@/molecules/Fragments/Pages/Product/ManagedShippingProductPage/typings';
import type ShippingProductProps from '@/molecules/Fragments/Pages/Product/Shipping/typings';

import {checkProductProviderHasPermission} from '@/database/auth';
import {fetchProductFeatureCollections, queryEditorsPick} from '@/database/editors-pick';
import {
  fetchActiveProviderHolidayFromDb,
  fetchActiveProviderSaleFromDb,
  fetchProductFromDb,
  fetchProductOfferableFromDb,
  fetchProviderReviewsSummaryFromDb,
  getCategoryHierarchy,
} from '@/database/product';

import {get} from '@/services/artfinder-service';
import cacheStore from '@/services/cache-service';
import {PRODUCT_GALLERY_SIZES, productGalleryImageSrcSetDef} from '@/services/image-loader';
import {
  fetchCurrencyConversionMapFromDb,
  serializeProduct,
  serializeProvider,
} from '@/services/product-service';

import useAuth from '@/hooks/use-auth/use-auth';
import useTranslation from '@/hooks/use-translation/use-translation';

import _ from '@/utils/_';
import constantsFactory from '@/utils/constants';
import {emit} from '@/utils/events';
import {
  historyChangedEvent,
  historyNewPagesChangedEvent,
  pushGAViewItemEvent,
  pushGAViewItemListEvent,
  pushProductDataForGTMVars,
} from '@/utils/gtm';
import i18n from '@/utils/i18n';
import {getPaintingJsonLd, getProductJsonLd} from '@/utils/jsonld';

import CollectionButton from '@/molecules/Buttons/Product/Collection/CollectionButton';
import FavouriteButton from '@/molecules/Buttons/Product/Favourite/FavouriteButton';
import ShareButton from '@/molecules/Buttons/Product/Share/ShareButton';
import ReturnPolicy from '@/molecules/Fragments/Pages/Product/ReturnPolicy/ReturnPolicy';
import PageLayout from '@/organisms/Layout/Page/PageLayout';
import {
  clearShippingInfo,
  getCustomerInfo,
  getMoreBySameArtist,
  getProductPrints,
  getShippingInfo,
  setCustomerInfo,
  setFavouriteCount,
  setProductPrints,
} from '@/redux/actions/productActions';
import {overrideCurrency, overrideUserLanguage} from '@/redux/actions/settingsActions';
import {impressions, pageView, visit} from '@/redux/actions/trackingActions';
import {selectProductData} from '@/redux/reducers/productReducer';
import {getRecentlyViewedProducts} from '@/redux/reducers/recentlyViewedProductsReducer';
import {CurrencyType, selectUserSettingsData} from '@/redux/reducers/userSettingsReducer';
import {initializeStore} from '@/redux/store';
import {getPrintsSSR} from '@/ssr/product/getPrints';
import MoreLikeThisSection from '@/templates/Sections/MoreLikeThis/MoreLikeThisSection';
import RecentlyViewedSection from '@/templates/Sections/RecentlyViewed/RecentlyViewedSection';
import cookie from '@/utils/helpers/cookie';
import './product.scss';
import {StyledBtnRow, StyledProductPage} from './styles';

const CtaStickyWithNoSSR = dynamic(() => import('@/molecules/Sticky/CTA/CtaSticky'), {ssr: false});

const TrustpilotReviewsWithNoSSR = dynamic(() => import('@/atoms/TrustPilot/TrustPilot'), {
  ssr: false,
});

const VideoPlayerWithNoSSR = dynamic(() => import('@/atoms/VideoPlayer/VideoPlayer'), {ssr: false});

const BtnAffirmWithNoSSR = dynamic(() => import('../../src/components/btn-affirm/btn-affirm'), {
  ssr: false,
});

const BtnDekoWithNoSSR = dynamic(() => import('../../src/components/btn-deko/btn-deko'), {
  ssr: false,
});

const ModalInstalmentsDekoWithNoSSR = dynamic(
  () => import('../../src/components/modal-instalments-deko/modal-instalments-deko'),
  {ssr: false},
);

const AddProductToBundleWithNoSSR = dynamic(
  () => import('@/molecules/AddProductToBundle/AddProductToBundle'),
  {ssr: false},
);

const RecommendedWidgetWithNoSSR = dynamic(
  () => import('../../src/components/recommended-widget/recommended-widget'),
  {ssr: false},
);

const ManagedShippingProductPageWithNoSSR = dynamic<ManagedShippingProductPageProps>(
  () =>
    import(
      '@/molecules/Fragments/Pages/Product/ManagedShippingProductPage/ManagedShippingProductPage'
    ),
  {ssr: false},
);

const ShippingProductPageWithNoSSR: React.ComponentType<ShippingProductProps> = dynamic(
  () => import('@/molecules/Fragments/Pages/Product/Shipping/ShippingProductPage'),
  {ssr: false},
);

const {CACHE, COOKIE} = constantsFactory();

const truncate = (length) => (str) => {
  if (str.length <= length) {
    return str;
  }

  return str.substr(0, length) + '...';
};
const truncate30 = truncate(30);
const truncate200 = truncate(200);

const ogTitleTemplateWithYear = () => `{name} ({year_made}) {category_full_name} by {artist_name}`;
const ogTitleTemplateNoYear = () => `{name} {category_full_name} by {artist_name}`;
const ogTitleTemplate = (x) => (x.year_made ? ogTitleTemplateWithYear() : ogTitleTemplateNoYear());

const artistUrl = (product) => `/artist/${product.artist_slug}/`;

const MINIMUM_AFFIRM_ORDER_VALUE = 200;
const MAXIMUM_AFFIRM_ORDER_VALUE = 30000;
const MINIMUM_DEKOPAY_ORDER_VALUE = 300;

const productType = {
  original: 'original',
  prints: 'prints',
} as const;

const ProductPage = ({
  productSlug,
  product,
  productDescription,
  productTags,
  productAutoTags,
  artist,
  editorsPicks,
  queryCurrency,
  activeProduct,
  isProviderManagedShipping,
}) => {
  // Hooks
  const auth = useAuth();
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {ref, inView} = useInView({
    threshold: 0.0,
  });

  // Selectors
  const settings = useSelector(selectUserSettingsData);
  const customerProductData = useSelector(selectProductData);
  const {prints} = useSelector(selectProductData);

  // Variables
  const [mltByArtist, setMltByArtist] = useState([]);
  const activeTab = useRef<'original' | 'prints'>(activeProduct);
  const [selectedPrint, setSelectedPrint] = useState(0);
  const [pdpExp, setPdpExp] = useState({
    experimentType: '',
    pdpbuytxt: '',
  });
  const prevCountryRef = useRef<string | undefined>();
  const recentlyViewed = useSelector(getRecentlyViewedProducts);

  const isManagedShipping =
    !!process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY && !!isProviderManagedShipping;

  /**
   * current selected product
   */
  const [selectedProduct, setSelectedProduct] = useState<PrintsData | ArtworkData | null>(null);
  const ctaArea = useRef<HTMLDivElement>(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  const [isTrustPilotError, setIsTrustPilotError] = useState<boolean>(false);

  useEffect(() => {
    if (queryCurrency && process.env.CURRENCY_COOKIE) {
      const isSecure = process.env.SECURE_COOKIE === 'true';
      cookie.set({
        name: process.env.CURRENCY_COOKIE,
        value: queryCurrency,
        options: {
          days: 365,
          secure: isSecure,
          domain: process.env.SESSION_COOKIE_DOMAIN,
          path: '/',
        },
      });
    }
  }, []);

  useEffect(() => {
    setSelectedProduct(
      activeTab.current === productType.original ? product : prints[selectedPrint],
    );
  }, [activeProduct, prints, selectedPrint]);

  const getLeadingCategory = (item) => {
    let result = 'painting';
    try {
      const sortedCategories = item.categories.sort((a, b) => (a.depth > b.depth ? 1 : -1));
      if (sortedCategories.length > 0) {
        const category = sortedCategories.slice(-1)[0];
        result = category?.slug;
      }
    } catch (e) {
      // ignore
    }

    return result;
  };

  const getCurrentAmount = (product, customerProductData) => {
    return customerProductData?.offerPricing
      ? customerProductData.offerPricing[settings.currency].amount
      : product.pricing[settings.currency].current_amount;
  };

  useEffect(() => {
    dispatch(getProductPrints(productSlug));
    const queryParams = _.queryStringParams(window.location.search.substr(1));
    const pdpExp = queryParams.get('pdpexp');
    const pdpbuytxt = queryParams.get('pdpbuytxt');

    if (pdpExp || pdpbuytxt) {
      setPdpExp({
        experimentType: pdpExp,
        pdpbuytxt: pdpbuytxt,
      });
    }

    if (queryParams.get('commission_request') === '1') {
      setTimeout(() => {
        emit('CLICK_COMMISSION_ARTIST');
      }, 0);
    }
    const moreByArtistSuccessCallbacks = [
      (data) => {
        setMltByArtist(data);
        pushGAViewItemListEvent(data, settings.currency);
        trackImpressions(data, false);
      },
    ];
    dispatch(getMoreBySameArtist(product.slug, moreByArtistSuccessCallbacks));
    dispatch(setFavouriteCount(product.interest_count));
    dispatch(getCustomerInfo(product.slug));
  }, []);

  useEffect(() => {
    trackImpressions(recentlyViewed.data, false);
  }, [recentlyViewed.data]);

  useEffect(() => {
    if (!selectedProduct) return;

    trackImpressions([selectedProduct], true);
    trackPageView();
    dispatch(visit('product', selectedProduct.id));
    pushProductDataForGTMVars(
      selectedProduct,
      settings.currency,
      product.artist_slug,
      product.category_slug,
    );
    pushGAViewItemEvent(
      selectedProduct,
      settings.currency,
      product.artist_slug,
      product.category_slug,
    );
  }, [selectedProduct]);

  useEffect(() => {
    if (prevCountryRef.current !== settings.country) {
      prevCountryRef.current = settings.country;
      get(`/api/product/${product.slug}/customer_info/`)
        .then((res) => {
          const payload = {
            freeShipping: res?.has_free_shipping,
          };

          dispatch(setCustomerInfo(payload));
        })
        .catch((err) => {
          console.log('customer info fetch error', err);
        });
    }
  }, [auth, settings]);

  useEffect(() => {
    const observedEl = ctaArea.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowStickyCTA(false);
          return;
        }

        if (entry.boundingClientRect.top < 0 && !showStickyCTA) {
          setShowStickyCTA(true);
        }
      },
      {
        threshold: [0.1],
      },
    );

    if (observedEl) observer.observe(observedEl);

    return () => {
      if (observedEl) observer.unobserve(observedEl);
    };
  }, [ctaArea]);

  const trackImpressions = (products, aboveFold: boolean) => {
    if (!products || !Array.isArray(products)) return;

    const url = `${window.location.pathname + window.location.hash}`;
    const productsToTrack = products.map((product) => ({
      above_fold: aboveFold,
      product_slug: product.slug,
      y_pos: 0,
    }));
    dispatch(impressions(url, productsToTrack));
  };

  const trackPageView = () => {
    const url = `${window.location.pathname + window.location.hash}`;
    dispatch(pageView(url));
  };

  return (
    <StyledProductPage
      className={
        'app page-product' +
        (activeProduct === productType.prints || !!prints?.length ? ' has-prints' : '') +
        (auth.isAuthenticated() ? ' is-authenticated' : '')
      }
    >
      <Head>
        <title>{`${product.name} ${product.category_full_name}: ${product.artist_name} | Artfinder`}</title>
        <meta
          name="description"
          content={t(
            'Buy {artwork_name} by {artist_name} on Artfinder. - An original {medium} {category} for sale. Support independent artists, buy today.',
            {
              artwork_name: truncate30(product.name),
              category: product.category_full_name,
              artist_name: product.artist_name,
              medium: product.style_name ?? '',
            },
          )}
        />
        <meta name="robots" content="index,follow" />
        <meta property="fb:app_id" content={process.env.SOCIAL_AUTH_FACEBOOK_KEY} />
        <meta property="og:site_name" content="Artfinder" />
        <meta property="og:title" content={t(ogTitleTemplate(product), product)} />
        <meta property="og:description" content={truncate200(product.description)} />
        <meta property="og:url" content={`${process.env.HOST}/product/${productSlug}/`} />
        <meta property="og:image" content={product?.images?.[0]?.url} />
        <meta property="og:image:width" content={product?.images?.[0]?.width} />
        <meta property="og:image:height" content={product?.images?.[0]?.height} />
        <meta name="twitter:image" content={product?.images?.[0]?.url} />
        <meta name="twitter:description" content={truncate200(product.description)} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@artfinder" />
        {artist.twitter_username && (
          <meta name="twitter:creator" content={`@${artist.twitter_username}`} />
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              getProductJsonLd(product, artist, queryCurrency ? queryCurrency : settings.currency),
            ),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              getPaintingJsonLd(product, artist, queryCurrency ? queryCurrency : settings.currency),
            ),
          }}
        />

        <link rel="canonical" href={`${process.env.HOST}/product/${productSlug}/`} />

        {product?.images?.[0]?.path && (
          <link
            rel="preload"
            as="image"
            type="image/webp"
            fetchPriority="high"
            imageSrcSet={productGalleryImageSrcSetDef(product.images[0].path)}
            imageSizes={PRODUCT_GALLERY_SIZES}
          />
        )}
      </Head>

      <PageLayout editorsPicks={editorsPicks}>
        <CtaStickyWithNoSSR
          show={showStickyCTA}
          product={selectedProduct}
          img={product?.images?.[0]?.thumbnail_url}
          title={product.name}
          currency={settings.currency}
          holiday={product?.holiday_prevent_checkout ?? false}
        />

        <Breadcrumb
          items={[
            {name: product.artist_name, url: artistUrl(product)},
            {name: 'All Artworks', url: artistUrl(product)},
            {name: product.name},
          ]}
          className="bootstrap-legacy"
        />

        <section className="bootstrap-legacy section-legacy">
          <div className="container">
            <div className="row">
              <div className="col-lg-8">
                <ProductCarouselFullScreen items={product.images} alt={product.name} />

                <StyledBtnRow>
                  <CollectionButton product={product} />

                  <ShareButton product={product} />

                  <FavouriteButton product={product} />
                </StyledBtnRow>

                <div className="desktop-product-bundle">
                  <ProductArtworkDescription
                    product={product}
                    productDescription={productDescription}
                    productTags={productTags}
                    productAutoTags={productAutoTags}
                  />
                </div>
              </div>

              <div className="col-lg-4">
                <Tab id="product-tab">
                  <ul className="nav nav-pills nav-justified" id="product-tab" role="tablist">
                    <li className="nav-item">
                      <a
                        className={
                          'nav-link' + (activeTab.current === productType.original ? ' active' : '')
                        }
                        data-toggle="tab"
                        href="#product-original"
                        id="product-original-tab"
                        role="tab"
                        aria-controls="product-original"
                        aria-selected="true"
                        onClick={() => {
                          activeTab.current = productType.original;
                          setSelectedProduct(product);
                          history.pushState(null, '', `/product/${productSlug}/`);
                          historyChangedEvent();
                          historyNewPagesChangedEvent();
                        }}
                      >
                        Original
                      </a>
                    </li>
                    <li className="nav-item tab-prints">
                      <a
                        className={
                          'nav-link' + (activeTab.current === productType.prints ? ' active' : '')
                        }
                        data-toggle="tab"
                        href="#product-prints"
                        id="product-prints-tab"
                        role="tab"
                        aria-controls="product-prints"
                        aria-selected="false"
                        onClick={() => {
                          activeTab.current = productType.prints;
                          setSelectedProduct(prints[selectedPrint]);
                          history.pushState(
                            null,
                            '',
                            `/product/${productSlug}/prints/${prints[selectedPrint].slug}/`,
                          );
                          historyChangedEvent();
                          historyNewPagesChangedEvent();
                        }}
                      >
                        Prints
                      </a>
                    </li>
                  </ul>
                </Tab>

                <div className="tab-content" ref={ctaArea}>
                  <div
                    className={
                      'tab-pane' + (activeTab.current === productType.original ? ' active' : '')
                    }
                    id="product-original"
                    role="tabpanel"
                    aria-labelledby="product-original-tab"
                  >
                    <ProductSummary
                      item={product}
                      settings={settings}
                      customerProductData={customerProductData}
                      pdpExp={pdpExp['experimentType']}
                    />

                    <ProductAttributes
                      item={product}
                      displayUnits={settings.display_units}
                      pdpExp={pdpExp['experimentType']}
                    />
                    <ProductAdditionalInfo
                      product={product}
                      hasPrints={!!prints?.length}
                      pdp={true}
                    />

                    {pdpExp['experimentType'] === 'urg' && product.is_in_stock && (
                      <>
                        {(product.in_sale && customerProductData.freeShipping && (
                          <>
                            <SaleCountdown
                              discountAmount={product.discount_amount}
                              countdownDate={product.sale_ends_datetime}
                            />
                            <FreeShipingCue />
                          </>
                        )) ||
                          (!customerProductData.freeShipping && product.in_sale && (
                            <>
                              <SaleCountdown
                                discountAmount={product.discount_amount}
                                countdownDate={product.sale_ends_datetime}
                              />

                              <Scarcity remaining={product.quantity} isUnique={product.unique} />
                            </>
                          )) ||
                          (!product.in_sale && customerProductData.freeShipping && (
                            <>
                              <FreeShipingCue />
                              <Scarcity remaining={product.quantity} isUnique={product.unique} />
                            </>
                          )) ||
                          (!customerProductData.freeShipping && !product.in_sale && (
                            <Scarcity remaining={product.quantity} isUnique={product.unique} />
                          ))}
                      </>
                    )}

                    {product.video_link && (
                      <div ref={ref} className="videoHolder-side">
                        {inView && (
                          <VideoPlayerWithNoSSR
                            videoUrl={product.video_link}
                            artworkName={product.name}
                            artistName={product.artist_name}
                          />
                        )}
                      </div>
                    )}

                    {pdpExp['experimentType'] === 'buy' && (
                      <AddToBasketCue productId={product.id} />
                    )}

                    <ProductBtnSet
                      pdpExp={pdpExp}
                      product={product}
                      hasPrints={!!prints?.length}
                      hideDislike={true}
                      pdp={true}
                    />
                  </div>
                  <div
                    className={
                      'tab-pane' + (activeTab.current === productType.prints ? ' active' : '')
                    }
                    id="product-prints"
                    role="tabpanel"
                    aria-labelledby="product-prints-tab"
                  >
                    <Print
                      prints={prints}
                      product={product}
                      selected={selectedPrint >= 0 ? selectedPrint : 0}
                      onChange={(x) => {
                        setSelectedPrint(x);
                        setSelectedProduct(prints[x]);
                        history.pushState(
                          null,
                          '',
                          `/product/${productSlug}/prints/${prints[x].slug}/`,
                        );
                        historyChangedEvent();
                        historyNewPagesChangedEvent();
                      }}
                      pdp={true}
                    />
                  </div>
                </div>

                {settings.country === 'US' &&
                  settings.currency === 'USD' &&
                  getCurrentAmount(product, customerProductData) >= MINIMUM_AFFIRM_ORDER_VALUE &&
                  getCurrentAmount(product, customerProductData) < MAXIMUM_AFFIRM_ORDER_VALUE && (
                    <InView triggerOnce threshold={1}>
                      {({inView, ref}) => (
                        <div ref={ref} style={{width: '100%'}}>
                          {inView && (
                            <BtnAffirmWithNoSSR
                              amountInCents={parseInt(
                                (100 * getCurrentAmount(product, customerProductData)).toFixed(0),
                              )}
                            />
                          )}
                        </div>
                      )}
                    </InView>
                  )}

                {process.env.DEKOPAY_ENABLED &&
                  settings.country === 'GB' &&
                  settings.currency === 'GBP' &&
                  getCurrentAmount(product, customerProductData) > MINIMUM_DEKOPAY_ORDER_VALUE && (
                    <InView triggerOnce threshold={1}>
                      {({inView, ref}) => (
                        <div ref={ref} style={{width: '100%'}}>
                          {inView && (
                            <BtnDekoWithNoSSR
                              settings={settings}
                              amount={parseInt(
                                getCurrentAmount(product, customerProductData).toFixed(0),
                              )}
                            />
                          )}
                        </div>
                      )}
                    </InView>
                  )}

                <div className="mobile-product-bundle">
                  <ProductArtworkDescription
                    product={product}
                    productDescription={productDescription}
                    productTags={productTags}
                    collapsible={true}
                  />
                </div>

                {/*
                 * Product Managed shipping enabled, based on:
                 * - feature flag provided, publishable key
                 * - product is supported by managed shipping, set by provider
                 * - otherwise, the regular shipping info will display
                 */}
                {selectedProduct && !isManagedShipping && (
                  <ShippingProductPageWithNoSSR
                    productType={activeTab.current as 'original' | 'prints'}
                    productSlug={selectedProduct.slug}
                    artistName={artist.name}
                    artworkName={product?.name}
                    reduxActions={{get: getShippingInfo, clear: clearShippingInfo}}
                    reduxStates={{product: selectProductData, settings: selectUserSettingsData}}
                  />
                )}
                {selectedProduct && isManagedShipping && (
                  <ManagedShippingProductPageWithNoSSR
                    selectedProductSlug={selectedProduct.slug}
                    categorySlug={product.category_slug}
                    providerSlug={artist.slug}
                    productType={activeTab.current as 'original' | 'prints'}
                  />
                )}

                <ReturnPolicy />

                {artist.review_data.review_count && <ProductArtistReviews artist={artist} />}

                {/* todo: review when the ticket is done
              https://artfinder-tech.atlassian.net/browse/BAUH-231 */}
                <br />
              </div>
            </div>
          </div>
        </section>

        <ProductArtist
          artist={artist}
          mltByArtist={mltByArtist}
          className="bootstrap-legacy section-legacy"
        />

        {process.env.TOGGLE_RECOMMENDATION_WIDGET_STATUS === 'enabled' && (
          <section className="bootstrap-legacy section-legacy">
            <div className="container">
              <RecommendedWidgetWithNoSSR
                productId={product.id}
                productPrice={product.original_currency_amount}
                subcat={getLeadingCategory(product)}
                style={product.style_slug}
                size="large"
              />
            </div>
          </section>
        )}

        <AddProductToBundleWithNoSSR productSlug={productSlug} />

        <MoreLikeThisSection
          recommendationId={process.env.NEXT_PUBLIC_BLOOMREACH_PDP_RECOMMENDATION_ID}
          productId={product.id}
        />

        <RecentlyViewedSection productId={product.id} />

        {!isTrustPilotError && (
          <section className="bootstrap-legacy section-legacy">
            <div className="container">
              <InView triggerOnce>
                {({inView, ref}) => (
                  <div ref={ref} style={{width: '100%'}}>
                    {inView && (
                      <TrustpilotReviewsWithNoSSR
                        onError={(errorMessage) => setIsTrustPilotError(!!errorMessage)}
                      />
                    )}
                  </div>
                )}
              </InView>
            </div>
          </section>
        )}
      </PageLayout>
      <div className="bootstrap-legacy">
        <ModalMakeOffer artist={artist} />
        <ModalInstalmentsDekoWithNoSSR />
        <ModalAddToCollection />
        <ModalCreateCollection />
        <ArtistContactMe artist={artist} />
        <ArtistCommission artist={artist} />
      </div>
    </StyledProductPage>
  );
};

export default ProductPage;

// eslint-disable-next-line
export const getServerSideProps = withRedirects(async (context) => {
  const reduxStore = initializeStore({});
  const productType = {
    original: 'original',
    prints: 'prints',
  } as const;
  const productSlug = context.params?.param?.[0];
  let locale: string | null = null;
  let queryCurrency: CurrencyType | null = null;
  if (context?.locale && context?.locale !== 'not-set') {
    locale = context.locale;
    queryCurrency = locale === 'en-GB' ? 'GBP' : 'USD';
    reduxStore.dispatch(overrideCurrency(queryCurrency));
  }

  if (locale) {
    /**
     * IF there is a locale in URL:
     * - Set user language in Redux store and i18n
     * - Update the `'af-locale'` cookie to match locale value
     */
    reduxStore.dispatch(overrideUserLanguage(locale));
    await i18n.changeLanguage(locale);
    context.res.setHeader(
      'Set-Cookie',
      `${COOKIE.LOCALE}=${locale.toLowerCase()};Domain=${process.env.DOMAIN};Path=/;Max-Age=${60 * 60 * 24 * 365}`,
    );
  }

  // Checking for prints
  const activeProduct = getSelectedProduct(context, productType);
  if (activeProduct === productType.prints && !!queryCurrency) {
    const productPrints = await getPrintsSSR(productSlug);
    reduxStore.dispatch(setProductPrints(productPrints));
  }

  // Redirect to new giftcard page
  if (productSlug === 'giftcard') {
    return {
      redirect: {
        destination: `${process.env.NEW_GIFTCARD_URL}`,
        permanent: true,
        basePath: false,
      },
    };
  }

  // Get editors pick from api or from db according to settings
  try {
    // redis cache
    let editorsPickFromCache = null;
    try {
      editorsPickFromCache = await cacheStore.get('last_5_editors_pick');
    } catch (e) {
      console.log(`[Product] Error. editors pick cache get() - ${e}`);
    }
    const promises: any[] = [];
    if (productSlug) {
      const productCall = fetchProductFromDb(productSlug);
      promises.push(productCall.then((r) => ({name: 'product', result: r})));
    }
    if (!editorsPickFromCache) {
      const editorsPickCall = queryEditorsPick();
      promises.push(editorsPickCall.then((r) => ({name: 'editorsPicks', result: r})));
    }

    const result = await Promise.all(promises);
    const productWithImages = result.filter((x) => x.name == 'product')?.[0]?.result;

    const editorsPicks =
      editorsPickFromCache ?? result.filter((x) => x.name == 'editorsPicks')?.[0]?.result;
    // Check if product does not exist or provider is hidden
    if (!productWithImages || productWithImages?.provider?.hidden === true) {
      return {
        redirect: {
          destination: `${process.env.HOST}/art/`,
          permanent: false,
          basePath: false,
        },
      };
    }

    // Check if product exists but is hidden, deleted or not published
    if (
      productWithImages?.provider?.slug &&
      (productWithImages?.hidden === true ||
        productWithImages?.deleted === true ||
        !productWithImages?.published_at)
    ) {
      return {
        redirect: {
          destination: `${process.env.HOST}/artist/${productWithImages.provider.slug}/`,
          permanent: true,
          basePath: false,
        },
      };
    }

    const conversionRates = await fetchCurrencyConversionMapFromDb();
    const productFeaturedCollections: any = await fetchProductFeatureCollections(
      productWithImages.id,
    );

    let categoryHierarchy: any[] = [];
    if (productWithImages?.category) {
      categoryHierarchy = await getCategoryHierarchy(productWithImages.category);
    }

    let activeProviderHoliday: any | null = null;
    let activeProviderSale: any | null = null;
    if (productWithImages?.provider) {
      activeProviderHoliday = await fetchActiveProviderHolidayFromDb(productWithImages.provider_id);
      activeProviderSale = await fetchActiveProviderSaleFromDb(productWithImages.provider_id);
    }

    let productOfferable: any | null = null;
    try {
      productOfferable = await fetchProductOfferableFromDb(productWithImages.id);
    } catch (e) {
      console.log(e);
    }

    const product: any = serializeProduct(
      productWithImages,
      categoryHierarchy,
      conversionRates,
      activeProviderHoliday,
      activeProviderSale,
      productFeaturedCollections,
      productOfferable,
    );

    const reviewsBreakdown = await fetchProviderReviewsSummaryFromDb(
      productWithImages?.provider_id,
    );
    const artist = serializeProvider(productWithImages, reviewsBreakdown);

    const productDescription: Array<string> = product?.description
      ? product.description.replace('\r\ng', '\n').split('\n')
      : [];
    const productTags: Array<string> = product?.tags
      ? product.tags.split(',').filter((x) => x)
      : [];
    const productAutoTags: Array<string> = product?.auto_tags
      ? product.auto_tags.split(',').filter((x) => x)
      : [];

    /**
     * @description Indicates if the product's provider has permission to use managed shipping.
     */
    const isProviderManagedShipping = !!productWithImages.managed_shipping_table_id
      ? await checkProductProviderHasPermission(
          'use_managed_shipping_table',
          productWithImages?.provider?.main_user_id,
        )
      : false;

    // set on cache anything we want
    if (!editorsPickFromCache) {
      try {
        await cacheStore.set(
          'last_5_editors_pick',
          editorsPicks,
          CACHE.EDITORS_PICK_CACHE_TTL_MILLISECONDS,
        );
      } catch (e) {
        console.log(`[Product] Error. editors pick cache set() - ${e}`);
      }
    }

    // Set cache headers
    if (context.res) {
      context.res.setHeader(
        'Cache-Control',
        `public, s-maxage=${CACHE.PRODUCT_CACHE_TTL_SECONDS}, stale-while-revalidate=${CACHE.PRODUCT_CACHE_STALE_WHILE_REVALIDATE_TTL_SECONDS}`,
      );
    }

    return {
      props: {
        productSlug,
        product,
        productDescription,
        productTags,
        productAutoTags,
        artist,
        editorsPicks,
        queryCurrency,
        initialReduxState: reduxStore.getState(),
        activeProduct,
        isProviderManagedShipping,
      },
    };
  } catch (error) {
    if ('detail' in error && error.detail.includes('Not found')) {
      return {
        redirect: {
          destination: `${process.env.HOST}/art/`,
          permanent: false,
          basePath: false,
        },
      };
    } else {
      console.log(
        `[Product] Error. Invalid Rendering - slug: [${productSlug}] (currency:${queryCurrency})`,
      );
      Bugsnag.notify(
        new Error(
          `[Product] Error. Invalid Rendering - slug: [${productSlug}] (currency:${queryCurrency})`,
        ),
        (event) => {
          event.severity = 'error';
          event.unhandled = true;
        },
      );

      return {
        redirect: {
          destination: `${process.env.HOST}/art/`,
          permanent: false,
          basePath: false,
        },
      };
    }
  }
});
const getSelectedProduct = (context, productType) => {
  const printSlugIndex =
    context.params.param.findIndex((param) => param === productType.prints) + 1;
  const printsSelected =
    printSlugIndex !== 0 && context.params.param[printSlugIndex].includes(context.params.param[0]);
  return printsSelected ? productType.prints : productType.original;
};

declare global {
  interface Window {
    expart012: any;
    af_experiment_012: boolean;
    af_experiment_012_text: string;
    dataLayer?: any[];
  }
}

interface PrintsData {
  id: number;
  slug: string;
}

interface ArtworkData {
  id: number;
  slug: string;
}
